import React, { useState, useEffect, useRef } from 'react';

const Stats = () => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [currentValues, setCurrentValues] = useState({});
  const statsRef = useRef(null);

  const statsData = [
    {
      id: "stat1",
      value: 12482,
      label: "Active Members",
      icon: "👥",
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50",
      borderColor: "border-blue-200",
      textColor: "text-blue-700",
      description: "Growing Daily"
    },
    {
      id: "stat2",
      value: 67,
      label: "Countries",
      icon: "🌍",
      color: "from-emerald-500 to-green-500",
      bgColor: "bg-gradient-to-br from-emerald-50 to-green-50",
      borderColor: "border-emerald-200",
      textColor: "text-emerald-700",
      description: "Global Presence"
    },
    {
      id: "stat3",
      value: 892,
      prefix: "$",
      label: "Avg Daily Earnings",
      icon: "💰",
      color: "from-amber-500 to-orange-500",
      bgColor: "bg-gradient-to-br from-amber-50 to-orange-50",
      borderColor: "border-amber-200",
      textColor: "text-amber-700",
      description: "Per Active Member"
    },
    {
      id: "stat4",
      value: 14,
      label: "Income Streams",
      icon: "🚀",
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
      borderColor: "border-purple-200",
      textColor: "text-purple-700",
      description: "Diverse Opportunities"
    }
  ];

  const animateValue = (id, start, end, duration) => {
    return new Promise((resolve) => {
      let startTimestamp = null;
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        
        const easeOutBack = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.floor(easeOutBack * (end - start) + start);
        
        setCurrentValues(prev => ({
          ...prev,
          [id]: currentValue
        }));
        
        if (progress < 1) {
          window.requestAnimationFrame(step);
        } else {
          resolve();
        }
      };
      window.requestAnimationFrame(step);
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (!hasAnimated) {
              setTimeout(async () => {
                for (const stat of statsData) {
                  await animateValue(stat.id, 0, stat.value, 1800);
                  await new Promise(resolve => setTimeout(resolve, 200));
                }
                setHasAnimated(true);
              }, 500);
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [hasAnimated]);

  const StatCard = ({ stat, index }) => (
    <div 
      className={`relative group transform transition-all duration-700 ${
        isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-10 opacity-0 scale-95'
      }`}
      style={{ transitionDelay: `${index * 200}ms` }}
    >
      {/* Main Card */}
      <div className={`
        relative ${stat.bgColor} backdrop-blur-sm
        p-8 rounded-3xl border-2 ${stat.borderColor}
        shadow-xl shadow-gray-200/60
        transform transition-all duration-500 group-hover:scale-105 group-hover:shadow-2xl
        hover:shadow-2xl hover:shadow-gray-300/40 text-center overflow-hidden
        min-w-[280px] mx-auto
        before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent before:-translate-x-full before:group-hover:translate-x-full before:transition-transform before:duration-1000
      `}>
        
        {/* Floating Icon */}
        <div className={`relative w-24 h-24 ${stat.bgColor} rounded-2xl flex items-center justify-center text-4xl mb-6 mx-auto shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 border-2 ${stat.borderColor} group-hover:shadow-xl`}>
          <div className={`bg-gradient-to-br ${stat.color} bg-clip-text text-transparent transform group-hover:scale-110 transition-transform duration-300`}>
            {stat.icon}
          </div>
          
          {/* Animated Ring */}
          <div className={`absolute inset-0 rounded-2xl border-2 ${stat.borderColor} opacity-0 group-hover:opacity-100 transform group-hover:scale-110 transition-all duration-500`}></div>
        </div>
        
        {/* Value Display */}
        <div className="relative z-10 mb-4">
          <div className="flex items-center justify-center space-x-1">
            {stat.prefix && (
              <span className={`text-4xl md:text-5xl font-black bg-gradient-to-br ${stat.color} bg-clip-text text-transparent drop-shadow-sm`}>
                {stat.prefix}
              </span>
            )}
            <span 
              className={`text-4xl md:text-5xl font-black bg-gradient-to-br ${stat.color} bg-clip-text text-transparent transition-all duration-300 drop-shadow-sm`}
            >
              {currentValues[stat.id]?.toLocaleString() || "0"}
            </span>
          </div>
          
          {/* Real-time Counter Dot */}
          <div className="absolute -top-2 -right-2 w-4 h-4 flex items-center justify-center">
            <div className={`w-2 h-2 bg-gradient-to-br ${stat.color} rounded-full animate-ping opacity-70`}></div>
            <div className={`absolute w-3 h-3 bg-gradient-to-br ${stat.color} rounded-full border-2 border-white shadow-sm`}></div>
          </div>
        </div>
        
        {/* Label */}
        <h3 className={`text-2xl font-bold mb-2 transition-all duration-500 group-hover:${stat.textColor} text-gray-900`}>
          {stat.label}
        </h3>
        
        {/* Description */}
        <p className={`text-sm font-semibold transition-all duration-500 group-hover:text-gray-700 text-gray-600 uppercase tracking-wider mb-6`}>
          {stat.description}
        </p>
        
        {/* Animated Progress Bar */}
        <div className="relative w-full bg-white/80 rounded-full h-3 overflow-hidden border border-gray-200 shadow-inner">
          <div 
            className={`h-full bg-gradient-to-r ${stat.color} rounded-full transition-all duration-2000 ease-out shadow-md relative overflow-hidden`}
            style={{ 
              width: isVisible ? '100%' : '0%',
              transitionDelay: `${index * 200 + 800}ms`
            }}
          >
            {/* Shimmer Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -skew-x-12 animate-shimmer"></div>
          </div>
          
          {/* Progress Dots */}
          <div className="absolute inset-0 flex justify-between items-center px-2">
            {[0, 1, 2, 3, 4].map((dot) => (
              <div 
                key={dot}
                className="w-1 h-1 bg-white/50 rounded-full"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transition: `opacity 0.3s ease ${index * 200 + 1000 + dot * 100}ms`
                }}
              ></div>
            ))}
          </div>
        </div>

        {/* Corner Accents */}
        <div className={`absolute top-4 left-4 w-3 h-3 bg-gradient-to-br ${stat.color} rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-150`}></div>
        <div className={`absolute bottom-4 right-4 w-3 h-3 bg-gradient-to-br ${stat.color} rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-150`}></div>
      </div>
      
      {/* Connection Arrows */}
      {index < statsData.length - 1 && (
        <div className="hidden xl:block absolute top-1/2 -right-8 transform -translate-y-1/2">
          <div className="w-16 h-0.5 bg-gradient-to-r from-gray-300 to-transparent group-hover:from-gray-400 transition-colors duration-300"></div>
          <div className={`absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-4 border-r-2 border-b-2 border-gray-300 group-hover:border-gray-400 rotate-[-45deg] transition-all duration-300`}></div>
        </div>
      )}
    </div>
  );

  return (
    <section 
      ref={statsRef}
      className="stats py-24 md:py-40 bg-gradient-to-br from-white via-blue-50/50 to-purple-50/30 relative overflow-hidden"
    >
      {/* Animated Background Elements */}
      <div className="absolute top-10 left-10 w-80 h-80 bg-blue-200/30 rounded-full blur-3xl animate-pulse-slow"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-amber-200/20 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '4s'}}></div>
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Enhanced Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-4 bg-white/80 backdrop-blur-md text-gray-700 px-8 py-4 rounded-2xl text-lg font-bold mb-8 shadow-xl shadow-blue-100/50 border border-gray-200/80 hover:shadow-2xl hover:border-gray-300 transition-all duration-500 transform hover:scale-105">
            <div className="flex items-center space-x-2">
              <span className="h-3 w-3 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-400"></span>
              <span className="h-3 w-3 bg-blue-500 rounded-full animate-pulse shadow-lg shadow-blue-400" style={{animationDelay: '700ms'}}></span>
              <span className="h-3 w-3 bg-purple-500 rounded-full animate-pulse shadow-lg shadow-purple-400" style={{animationDelay: '1400ms'}}></span>
            </div>
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-black tracking-wide">
              LIVE COMMUNITY METRICS
            </span>
            <div className="flex items-center space-x-2">
              <span className="h-3 w-3 bg-purple-500 rounded-full animate-pulse shadow-lg shadow-purple-400"></span>
              <span className="h-3 w-3 bg-blue-500 rounded-full animate-pulse shadow-lg shadow-blue-400" style={{animationDelay: '700ms'}}></span>
              <span className="h-3 w-3 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-400" style={{animationDelay: '1400ms'}}></span>
            </div>
          </div>
          
          <h2 className="text-5xl md:text-7xl font-black mb-8 leading-tight">
            <span className="bg-gradient-to-r from-gray-900 via-blue-700 to-purple-700 bg-clip-text text-transparent drop-shadow-sm">
              Real Results,
            </span>
            <span className="block mt-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent drop-shadow-sm">
              Real Success
            </span>
          </h2>
          
          <p className="text-2xl md:text-3xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-light mb-6">
            Join <span className="font-semibold text-blue-600">12,482+</span> members already transforming their financial future
          </p>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            Actual performance data from our global community. No inflated numbers, just real achievements.
          </p>
        </div>

        {/* Enhanced Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 max-w-7xl mx-auto mb-24">
          {statsData.map((stat, index) => (
            <StatCard key={stat.id} stat={stat} index={index} />
          ))}
        </div>

        {/* Enhanced Trust Indicators */}
        <div className="mb-20">
          <div className="inline-flex flex-col sm:flex-row items-center justify-center space-y-6 sm:space-y-0 sm:space-x-16 text-gray-700 bg-white/90 backdrop-blur-xl py-8 px-12 rounded-3xl border border-gray-200/80 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:border-gray-300/80">
            {[
              { icon: "✅", text: "Verified Real Members", color: "text-emerald-500", count: "12.4K+" },
              { icon: "🛡️", text: "Secure & Regulated", color: "text-blue-500" },
              { icon: "💎", text: "Proven Track Record", color: "text-purple-500", count: "2+ Years" },
              { icon: "⚡", text: "Instant Withdrawals", color: "text-amber-500" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center space-x-4 group cursor-pointer transform hover:scale-105 transition-all duration-300">
                <span className={`text-2xl transform group-hover:scale-125 transition-transform duration-300 ${item.color}`}>
                  {item.icon}
                </span>
                <div className="text-left">
                  <div className="font-semibold group-hover:text-gray-900 transition-colors duration-300">
                    {item.text}
                  </div>
                  {item.count && (
                    <div className="text-sm font-bold text-gray-500 group-hover:text-gray-700">
                      {item.count}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced CTA */}
        <div className="text-center">
          <div className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 p-1 rounded-3xl shadow-2xl shadow-amber-500/30 hover:shadow-amber-500/50 transition-all duration-500 transform hover:scale-105 group">
            <a 
              href="https://www.vumaqash.com/user/register.php?ref=CLEOPHAS"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-4 bg-white text-gray-900 font-black text-xl py-5 px-12 rounded-2xl transition-all duration-300 group-hover:bg-gray-50"
            >
              <span className="text-2xl transform group-hover:rotate-180 transition-transform duration-500">🚀</span>
              <span>Join {statsData[0].value.toLocaleString()} Successful Members</span>
              <span className="text-xl transform group-hover:translate-x-2 transition-transform duration-300">→</span>
            </a>
          </div>
          
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-8 text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></span>
              <span>No monthly fees</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="h-2 w-2 bg-blue-500 rounded-full animate-pulse" style={{animationDelay: '500ms'}}></span>
              <span>24/7 expert support</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="h-2 w-2 bg-purple-500 rounded-full animate-pulse" style={{animationDelay: '1000ms'}}></span>
              <span>Start with just $50</span>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Custom Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(180deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-shimmer {
          animation: shimmer 2.5s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default Stats;